// Project-scoped operational settings — call hours, retry policy, caller
// identity, agent persona / voice / prompts, dry-run safety. Launched
// from the gear icon next to the workspace switcher in the topbar.
//
// Non-dismissible (no close-on-escape, no close-on-interact-outside) so
// a half-typed change can't be lost by accidentally clicking outside.
// Save and Cancel are the only ways out; Cancel restores the form to
// the server state.
//
// Voice tuning + Conversation prompts still hit the legacy
// /api/settings/retell-voice + /api/settings/agent-prompts endpoints
// since they edit the SINGLE shared Retell agent today. Per-project
// Retell agents are a Phase 4 deliverable; until then "Voice tuning"
// changes apply globally across every project (called out in the help
// text on those sections).

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Field,
  HStack,
  Heading,
  Input,
  NativeSelect,
  Portal,
  RadioGroup,
  Slider,
  Stack,
  Switch,
  Tabs,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useMe } from "@/hooks/useAuth";
import {
  useArchiveProject,
  useMyWorkspaces,
} from "@/hooks/useWorkspaces";
import {
  useProjectSettings,
  useUpdateProjectSettings,
  type ProjectSettings,
} from "@/hooks/useProjectSettings";
import ConfirmDialog from "./ConfirmDialog";
import {
  useAgentPrompts,
  useRetellVoice,
  useUpdateAgentPrompts,
  useUpdateRetellVoice,
  type RetellVoiceConfig,
} from "@/hooks/useSettings";

const APPOINTMENT_LENGTHS = [15, 30, 45, 60, 120];
const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Fields on ProjectSettings the modal lets you edit. Anything not in this
// list (telnyx*, retell*, projectId, updatedAt) is read-only / Phase 4.
const TRACKED_FIELDS: ReadonlyArray<keyof ProjectSettings> = [
  "defaultAppointmentMin",
  "agentGender",
  "agentVoiceId",
  "maxRetries",
  "retryCooldownHours",
  "campaignDefaultMode",
  "callHoursStart",
  "callHoursEnd",
  "callDays",
  "recordingDisclosureMsg",
  "callerFirstName",
  "callerLastName",
  "callerBusinessName",
  "agentPersonaName",
  "dryRunEnabled",
  "dryRunPhoneNumber",
  "dryRunEmail",
  "inboundEnabled",
  "retellAgentId",
  "retellInboundAgentId",
  "telnyxInboundNumber",
];

function pickTracked(s: ProjectSettings): Partial<ProjectSettings> {
  const out: Record<string, unknown> = {};
  for (const k of TRACKED_FIELDS) out[k] = (s as unknown as Record<string, unknown>)[k];
  return out as Partial<ProjectSettings>;
}

function diffPatch(
  form: Partial<ProjectSettings>,
  base: ProjectSettings,
): Partial<ProjectSettings> {
  const patch: Record<string, unknown> = {};
  for (const k of TRACKED_FIELDS) {
    const a = (form as Record<string, unknown>)[k];
    const b = (base as unknown as Record<string, unknown>)[k];
    if (JSON.stringify(a) !== JSON.stringify(b)) patch[k] = a;
  }
  return patch as Partial<ProjectSettings>;
}

function showToast(kind: "success" | "error", text: string) {
  if (typeof document === "undefined") return;
  const el = document.createElement("div");
  el.textContent = text;
  el.style.cssText = `
    position: fixed; top: 16px; right: 16px; z-index: 9999;
    padding: 10px 14px; border-radius: 8px; font: 500 14px system-ui;
    color: #fff; box-shadow: 0 4px 12px rgba(0,0,0,0.25);
    background: ${kind === "success" ? "#16a34a" : "#dc2626"};
  `;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 2500);
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ProjectSettingsModal({ open, onOpenChange }: Props) {
  const { data: settings } = useProjectSettings({ enabled: open });
  const update = useUpdateProjectSettings();
  // For the archive action we need to know which workspace the active
  // project lives in, and whether the caller can perform admin actions
  // there (gates the Archive button).
  const { data: me } = useMe();
  const { data: workspacesData } = useMyWorkspaces();
  const archiveProject = useArchiveProject();
  const activeWs = workspacesData?.workspaces.find((w) =>
    w.projects.some((p) => p.id === me?.lastActiveProjectId),
  );
  const canArchive =
    activeWs?.role === "OWNER" || activeWs?.role === "ADMIN";
  const [activeTab, setActiveTab] = useState("hours");

  const [form, setForm] = useState<Partial<ProjectSettings> | null>(null);
  // telnyxApiKey isn't on `form` — server returns only hasTelnyxApiKey,
  // never the ciphertext. Local state tracks whether the operator has
  // typed a new value this session.
  const [telnyxApiKeyDraft, setTelnyxApiKeyDraft] = useState<string | null>(null);
  const [clearApiKey, setClearApiKey] = useState(false);

  const promptsQ = useAgentPrompts();
  const updatePrompts = useUpdateAgentPrompts();
  const [outboundPrompt, setOutboundPrompt] = useState<string>("");
  const [inboundPrompt, setInboundPrompt] = useState<string>("");

  const retellVoiceQ = useRetellVoice();
  const updateRetellVoice = useUpdateRetellVoice();
  const [voiceForm, setVoiceForm] = useState<RetellVoiceConfig | null>(null);

  useEffect(() => {
    if (settings) {
      setForm(pickTracked(settings));
      setTelnyxApiKeyDraft(null);
      setClearApiKey(false);
    }
  }, [settings]);

  useEffect(() => {
    if (promptsQ.data) {
      setOutboundPrompt(promptsQ.data.outbound);
      setInboundPrompt(promptsQ.data.inbound);
    }
  }, [promptsQ.data]);

  useEffect(() => {
    if (retellVoiceQ.data) setVoiceForm(retellVoiceQ.data);
  }, [retellVoiceQ.data]);

  const promptsDirty = useMemo(() => {
    if (!promptsQ.data) return false;
    return (
      outboundPrompt !== promptsQ.data.outbound ||
      inboundPrompt !== promptsQ.data.inbound
    );
  }, [outboundPrompt, inboundPrompt, promptsQ.data]);

  const voiceDirty = useMemo(() => {
    if (!voiceForm || !retellVoiceQ.data) return false;
    return (
      voiceForm.voiceSpeed !== retellVoiceQ.data.voiceSpeed ||
      voiceForm.interruptionSensitivity !== retellVoiceQ.data.interruptionSensitivity
    );
  }, [voiceForm, retellVoiceQ.data]);

  const apiKeyDirty = telnyxApiKeyDraft !== null || clearApiKey;
  const [archiveOpen, setArchiveOpen] = useState(false);

  const dirty = useMemo(() => {
    if (!form || !settings) return false;
    if (promptsDirty || voiceDirty || apiKeyDirty) return true;
    return Object.keys(diffPatch(form, settings)).length > 0;
  }, [form, settings, promptsDirty, voiceDirty, apiKeyDirty]);

  function set<K extends keyof ProjectSettings>(key: K, value: ProjectSettings[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  function discard() {
    if (settings) setForm(pickTracked(settings));
    if (promptsQ.data) {
      setOutboundPrompt(promptsQ.data.outbound);
      setInboundPrompt(promptsQ.data.inbound);
    }
    if (retellVoiceQ.data) setVoiceForm(retellVoiceQ.data);
    setTelnyxApiKeyDraft(null);
    setClearApiKey(false);
  }

  async function save() {
    if (!settings || !form) return;
    const patch: Record<string, unknown> = { ...diffPatch(form, settings) };
    if (telnyxApiKeyDraft !== null) {
      patch.telnyxApiKey = telnyxApiKeyDraft;
    } else if (clearApiKey) {
      patch.telnyxApiKey = null;
    }
    try {
      const ops: Promise<unknown>[] = [];
      if (Object.keys(patch).length > 0) {
        ops.push(update.mutateAsync(patch));
      }
      if (promptsDirty) {
        const promptPatch: Partial<{ outbound: string; inbound: string }> = {};
        if (outboundPrompt !== promptsQ.data?.outbound) {
          promptPatch.outbound = outboundPrompt;
        }
        if (inboundPrompt !== promptsQ.data?.inbound) {
          promptPatch.inbound = inboundPrompt;
        }
        ops.push(updatePrompts.mutateAsync(promptPatch));
      }
      if (voiceDirty && voiceForm) {
        ops.push(updateRetellVoice.mutateAsync(voiceForm));
      }
      await Promise.all(ops);
      showToast("success", "Project settings saved");
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save";
      showToast("error", msg);
    }
  }

  function cancel() {
    discard();
    onOpenChange(false);
  }

  function toggleDay(day: number) {
    const cur = form?.callDays ?? [];
    const next = cur.includes(day)
      ? cur.filter((d) => d !== day)
      : [...cur, day].sort();
    set("callDays", next);
  }

  return (
    <Dialog.Root
      open={open}
      // Non-dismissible: explicit Save / Cancel are the only exits.
      closeOnEscape={false}
      closeOnInteractOutside={false}
      onOpenChange={(d) => {
        // Block all auto-close paths; only our cancel/save handlers should
        // ever flip open back to false.
        if (d.open === false) return;
        onOpenChange(d.open);
      }}
      size="xl"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxW="3xl" maxH="90dvh" display="flex" flexDirection="column">
            <Dialog.Header>
              <Dialog.Title>Project settings</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body flex="1" overflowY="auto">
              {!settings || !form ? (
                <Text color="fg.muted">Loading…</Text>
              ) : (
                <Tabs.Root
                  value={activeTab}
                  onValueChange={(d) => setActiveTab(d.value)}
                  variant="enclosed"
                >
                  <Tabs.List>
                    <Tabs.Trigger value="hours">Call hours</Tabs.Trigger>
                    <Tabs.Trigger value="retries">Retry policy</Tabs.Trigger>
                    <Tabs.Trigger value="caller">Caller identity</Tabs.Trigger>
                    <Tabs.Trigger value="agent">Agent</Tabs.Trigger>
                    <Tabs.Trigger value="dryrun">Dry-run</Tabs.Trigger>
                    <Tabs.Trigger value="telephony">Telephony</Tabs.Trigger>
                    <Tabs.Trigger value="other">Other</Tabs.Trigger>
                  </Tabs.List>

                  <Tabs.Content value="hours">
                    <Stack gap={6} pt={4}>
                      <Field.Root>
                        <Field.Label>Default appointment length</Field.Label>
                        <NativeSelect.Root size="sm" w={48}>
                          <NativeSelect.Field
                            value={form.defaultAppointmentMin}
                            onChange={(e) =>
                              set(
                                "defaultAppointmentMin",
                                Number.parseInt(e.target.value, 10),
                              )
                            }
                          >
                            {APPOINTMENT_LENGTHS.map((n) => (
                              <option key={n} value={n}>
                                {n < 60 ? `${n} min` : `${n / 60} hr${n === 60 ? "" : "s"}`}
                              </option>
                            ))}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>

                      <Stack gap={3}>
                        <Heading size="sm">Call hours</Heading>
                        <HStack gap={4}>
                          <Field.Root>
                            <Field.Label>Start</Field.Label>
                            <Input
                              type="time"
                              value={form.callHoursStart}
                              onChange={(e) => set("callHoursStart", e.target.value)}
                              w={32}
                            />
                          </Field.Root>
                          <Field.Root>
                            <Field.Label>End</Field.Label>
                            <Input
                              type="time"
                              value={form.callHoursEnd}
                              onChange={(e) => set("callHoursEnd", e.target.value)}
                              w={32}
                            />
                          </Field.Root>
                        </HStack>
                        <Field.Root>
                          <Field.Label>Active days</Field.Label>
                          <HStack>
                            {DAY_LABELS.map((l, i) => {
                              const active = (form.callDays ?? []).includes(i);
                              return (
                                <Button
                                  key={i}
                                  size="sm"
                                  colorPalette={active ? "blue" : "gray"}
                                  variant={active ? "solid" : "outline"}
                                  onClick={() => toggleDay(i)}
                                  aria-label={DAY_NAMES[i]}
                                >
                                  {l}
                                </Button>
                              );
                            })}
                          </HStack>
                        </Field.Root>
                      </Stack>
                    </Stack>
                  </Tabs.Content>

                  <Tabs.Content value="retries">
                    <Stack gap={6} pt={4}>
                      <Field.Root>
                        <Field.Label>Max retry attempts per client</Field.Label>
                        <Input
                          type="number"
                          min={1}
                          max={20}
                          value={form.maxRetries}
                          onChange={(e) =>
                            set("maxRetries", Number.parseInt(e.target.value || "1", 10))
                          }
                          w={32}
                        />
                        <Field.HelperText>
                          After this many no-answer / busy / soft-decline outcomes
                          we'll stop retrying and notify you to call manually.
                        </Field.HelperText>
                      </Field.Root>
                      <Field.Root>
                        <Field.Label>Cooldown between attempts (hours)</Field.Label>
                        <Input
                          type="number"
                          min={1}
                          max={168}
                          value={form.retryCooldownHours}
                          onChange={(e) =>
                            set(
                              "retryCooldownHours",
                              Number.parseInt(e.target.value || "1", 10),
                            )
                          }
                          w={32}
                        />
                        <Field.HelperText>
                          Minimum wait before a client is re-dialled after a
                          previous attempt. Snapped to your next call window.
                        </Field.HelperText>
                      </Field.Root>
                    </Stack>
                  </Tabs.Content>

                  <Tabs.Content value="caller">
                    <Stack gap={4} pt={4}>
                      <Text fontSize="sm" color="fg.muted">
                        These names are spoken by the AI on every call —
                        "Hi, I'm calling on behalf of <b>{form.callerFirstName} {form.callerLastName}</b> from <b>{form.callerBusinessName || "your business"}</b>."
                      </Text>
                      <HStack gap={3}>
                        <Field.Root>
                          <Field.Label>First name</Field.Label>
                          <Input
                            value={form.callerFirstName ?? ""}
                            onChange={(e) => set("callerFirstName", e.target.value)}
                          />
                        </Field.Root>
                        <Field.Root>
                          <Field.Label>Last name</Field.Label>
                          <Input
                            value={form.callerLastName ?? ""}
                            onChange={(e) => set("callerLastName", e.target.value)}
                          />
                        </Field.Root>
                      </HStack>
                      <Field.Root>
                        <Field.Label>Business name</Field.Label>
                        <Input
                          value={form.callerBusinessName ?? ""}
                          onChange={(e) => set("callerBusinessName", e.target.value)}
                          placeholder="e.g. Skye Apartments"
                        />
                      </Field.Root>
                      <Field.Root>
                        <Field.Label>Recording disclosure</Field.Label>
                        <Textarea
                          rows={2}
                          value={form.recordingDisclosureMsg ?? ""}
                          onChange={(e) =>
                            set("recordingDisclosureMsg", e.target.value)
                          }
                          placeholder="This call may be recorded for quality and record-keeping."
                        />
                        <Field.HelperText>
                          Spoken at the start of every call. Defaults to a
                          standard disclosure if left blank.
                        </Field.HelperText>
                      </Field.Root>
                    </Stack>
                  </Tabs.Content>

                  <Tabs.Content value="agent">
                    <Stack gap={6} pt={4}>
                      <Field.Root>
                        <Field.Label>Agent's name</Field.Label>
                        <Input
                          value={form.agentPersonaName ?? ""}
                          onChange={(e) => set("agentPersonaName", e.target.value)}
                          maxLength={60}
                          placeholder="Nicole"
                        />
                        <Field.HelperText>
                          What the AI calls itself ("Hi, I'm <b>{form.agentPersonaName || "Nicole"}</b>…").
                        </Field.HelperText>
                      </Field.Root>

                      <Field.Root>
                        <Field.Label>Voice gender</Field.Label>
                        <RadioGroup.Root
                          value={form.agentGender}
                          onValueChange={(d) =>
                            d.value && set("agentGender", d.value as "MALE" | "FEMALE")
                          }
                        >
                          <HStack gap={6}>
                            <RadioGroup.Item value="FEMALE">
                              <RadioGroup.ItemHiddenInput />
                              <RadioGroup.ItemIndicator />
                              <RadioGroup.ItemText>Female</RadioGroup.ItemText>
                            </RadioGroup.Item>
                            <RadioGroup.Item value="MALE">
                              <RadioGroup.ItemHiddenInput />
                              <RadioGroup.ItemIndicator />
                              <RadioGroup.ItemText>Male</RadioGroup.ItemText>
                            </RadioGroup.Item>
                          </HStack>
                        </RadioGroup.Root>
                      </Field.Root>

                      {voiceForm ? (
                        <Stack gap={4}>
                          <Heading size="sm">Voice tuning</Heading>
                          <Text fontSize="xs" color="fg.muted">
                            Targets this project's bound Retell agent
                            (Project settings → Telephony). If the project
                            hasn't bound one, the workspace-wide
                            RETELL_AGENT_ID is used instead.
                          </Text>
                          <Field.Root>
                            <Field.Label>
                              Voice speed: {voiceForm.voiceSpeed.toFixed(2)}×
                            </Field.Label>
                            {/* Slider needs an explicit width wrapper —
                                without it the Track shrinks to the Thumb
                                size and the slider looks like a single
                                disc. <Slider.HiddenInput /> inside Thumb
                                is what makes the control draggable +
                                keyboard-accessible. */}
                            <Box w="full" maxW="md">
                              <Slider.Root
                                min={0.5}
                                max={1.5}
                                step={0.05}
                                value={[voiceForm.voiceSpeed]}
                                onValueChange={(d) => {
                                  const v = d.value[0];
                                  if (typeof v === "number") {
                                    setVoiceForm((p) => (p ? { ...p, voiceSpeed: v } : p));
                                  }
                                }}
                              >
                                <Slider.Control>
                                  <Slider.Track>
                                    <Slider.Range />
                                  </Slider.Track>
                                  <Slider.Thumb index={0}>
                                    <Slider.HiddenInput />
                                  </Slider.Thumb>
                                </Slider.Control>
                              </Slider.Root>
                            </Box>
                          </Field.Root>
                          <Field.Root>
                            <Field.Label>
                              Interruption sensitivity: {voiceForm.interruptionSensitivity.toFixed(2)}
                            </Field.Label>
                            <Box w="full" maxW="md">
                            <Slider.Root
                              min={0}
                              max={1}
                              step={0.05}
                              value={[voiceForm.interruptionSensitivity]}
                              onValueChange={(d) => {
                                const v = d.value[0];
                                if (typeof v === "number") {
                                  setVoiceForm((p) =>
                                    p ? { ...p, interruptionSensitivity: v } : p,
                                  );
                                }
                              }}
                            >
                              <Slider.Control>
                                <Slider.Track>
                                  <Slider.Range />
                                </Slider.Track>
                                <Slider.Thumb index={0}>
                                  <Slider.HiddenInput />
                                </Slider.Thumb>
                              </Slider.Control>
                            </Slider.Root>
                            </Box>
                          </Field.Root>
                        </Stack>
                      ) : null}

                      <Stack gap={3}>
                        <Heading size="sm">Conversation prompts</Heading>
                        <Text fontSize="xs" color="fg.muted">
                          Targets this project's bound Retell agents.
                          Outbound prompt edits the agent that handles
                          calls you dial; inbound prompt edits the agent
                          that answers calls to your Telnyx number.
                        </Text>
                        <Field.Root>
                          <Field.Label>Outbound prompt</Field.Label>
                          <Textarea
                            value={outboundPrompt}
                            onChange={(e) => setOutboundPrompt(e.target.value)}
                            rows={10}
                            fontFamily="mono"
                            fontSize="xs"
                          />
                        </Field.Root>
                        <Field.Root>
                          <Field.Label>Inbound prompt</Field.Label>
                          <Textarea
                            value={inboundPrompt}
                            onChange={(e) => setInboundPrompt(e.target.value)}
                            rows={10}
                            fontFamily="mono"
                            fontSize="xs"
                          />
                        </Field.Root>
                      </Stack>
                    </Stack>
                  </Tabs.Content>

                  <Tabs.Content value="dryrun">
                    <Stack gap={5} pt={4}>
                      <Field.Root>
                        <HStack>
                          <Switch.Root
                            checked={!!form.dryRunEnabled}
                            onCheckedChange={(d) => set("dryRunEnabled", d.checked)}
                          >
                            <Switch.HiddenInput />
                            <Switch.Control />
                          </Switch.Root>
                          <Field.Label mb={0}>Dry-run mode</Field.Label>
                        </HStack>
                        <Field.HelperText>
                          When on, every outbound call goes to the test number
                          below instead of the real client. Emails go to the
                          test inbox. Real clients are never contacted.
                        </Field.HelperText>
                      </Field.Root>
                      <Field.Root>
                        <Field.Label>Test phone number (E.164)</Field.Label>
                        <Input
                          value={form.dryRunPhoneNumber ?? ""}
                          onChange={(e) => set("dryRunPhoneNumber", e.target.value)}
                          placeholder="+61412345678"
                        />
                      </Field.Root>
                      <Field.Root>
                        <Field.Label>Test email inbox</Field.Label>
                        <Input
                          value={form.dryRunEmail ?? ""}
                          onChange={(e) => set("dryRunEmail", e.target.value)}
                          placeholder="you@example.com"
                        />
                      </Field.Root>
                    </Stack>
                  </Tabs.Content>

                  <Tabs.Content value="telephony">
                    <Stack gap={5} pt={4}>
                      <Text fontSize="sm" color="fg.muted">
                        Per-project Telnyx number + Retell agents. Leave
                        blank to fall back to the workspace-wide default
                        from environment config (suitable for solo
                        operators with one number).
                      </Text>

                      <Stack gap={3}>
                        <Heading size="sm">Telnyx</Heading>
                        <Field.Root>
                          <Field.Label>Inbound number (E.164)</Field.Label>
                          <Input
                            value={form.telnyxInboundNumber ?? ""}
                            onChange={(e) =>
                              set("telnyxInboundNumber", e.target.value || null)
                            }
                            placeholder="+61755012345"
                          />
                          <Field.HelperText>
                            Inbound webhooks for this number get routed
                            to this project. Also used as the outbound
                            from-number when this project dials out.
                          </Field.HelperText>
                        </Field.Root>

                        <Field.Root>
                          <Field.Label>API key</Field.Label>
                          {settings?.hasTelnyxApiKey && telnyxApiKeyDraft === null && !clearApiKey ? (
                            <HStack gap={2}>
                              <Input
                                value="••••••••••••••••"
                                readOnly
                                fontFamily="mono"
                                fontSize="sm"
                                maxW="md"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setTelnyxApiKeyDraft("")}
                              >
                                Replace
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                colorPalette="red"
                                onClick={() => setClearApiKey(true)}
                              >
                                Clear
                              </Button>
                            </HStack>
                          ) : (
                            <HStack gap={2}>
                              <Input
                                type="password"
                                value={telnyxApiKeyDraft ?? ""}
                                onChange={(e) => {
                                  setTelnyxApiKeyDraft(e.target.value);
                                  setClearApiKey(false);
                                }}
                                placeholder="KEY01234567…"
                                fontFamily="mono"
                                fontSize="sm"
                                maxW="md"
                              />
                              {settings?.hasTelnyxApiKey || clearApiKey ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setTelnyxApiKeyDraft(null);
                                    setClearApiKey(false);
                                  }}
                                >
                                  Keep current
                                </Button>
                              ) : null}
                            </HStack>
                          )}
                          <Field.HelperText>
                            Stored encrypted at rest (AES-256-GCM). Never
                            sent back to the browser — the dots are a
                            placeholder, not the real value.
                            {clearApiKey ? (
                              <Text as="span" color="orange.400" ml={1}>
                                Will be cleared on save.
                              </Text>
                            ) : null}
                          </Field.HelperText>
                        </Field.Root>
                      </Stack>

                      <Stack gap={3}>
                        <Heading size="sm">Retell agents</Heading>
                        <Field.Root>
                          <Field.Label>Outbound agent ID</Field.Label>
                          <Input
                            value={form.retellAgentId ?? ""}
                            onChange={(e) =>
                              set("retellAgentId", e.target.value || null)
                            }
                            placeholder="agent_xxxxxxxxxxxxxxxxxxxxxx"
                            fontFamily="mono"
                            fontSize="sm"
                          />
                        </Field.Root>
                        <Field.Root>
                          <Field.Label>Inbound agent ID</Field.Label>
                          <Input
                            value={form.retellInboundAgentId ?? ""}
                            onChange={(e) =>
                              set("retellInboundAgentId", e.target.value || null)
                            }
                            placeholder="agent_xxxxxxxxxxxxxxxxxxxxxx"
                            fontFamily="mono"
                            fontSize="sm"
                          />
                          <Field.HelperText>
                            Get these from your Retell dashboard. The
                            outbound agent handles calls you dial; the
                            inbound agent answers calls to your Telnyx
                            number.
                          </Field.HelperText>
                        </Field.Root>
                      </Stack>
                    </Stack>
                  </Tabs.Content>

                  <Tabs.Content value="other">
                    <Stack gap={5} pt={4}>
                      <Field.Root>
                        <Field.Label>Default campaign mode</Field.Label>
                        <NativeSelect.Root size="sm" w={48}>
                          <NativeSelect.Field
                            value={form.campaignDefaultMode}
                            onChange={(e) =>
                              set(
                                "campaignDefaultMode",
                                e.target.value as "LIVE" | "SIMULATION",
                              )
                            }
                          >
                            <option value="SIMULATION">Simulation</option>
                            <option value="LIVE">Live</option>
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                        <Field.HelperText>
                          Pre-selected in the Call-all dialog. Simulation is
                          the safe default — operator must explicitly switch
                          to Live each time.
                        </Field.HelperText>
                      </Field.Root>
                      <Field.Root>
                        <HStack>
                          <Checkbox.Root
                            checked={!!form.inboundEnabled}
                            onCheckedChange={(d) =>
                              set("inboundEnabled", d.checked === true)
                            }
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control />
                          </Checkbox.Root>
                          <Field.Label mb={0}>Accept inbound calls</Field.Label>
                        </HStack>
                        <Field.HelperText>
                          When on, customers calling the project's Telnyx
                          number get answered by the inbound agent. Off
                          rejects them with a busy signal.
                        </Field.HelperText>
                      </Field.Root>

                      {canArchive && activeWs && me?.lastActiveProjectId ? (
                        <Stack
                          gap={2}
                          pt={5}
                          mt={3}
                          borderTopWidth="1px"
                          borderColor="border.muted"
                        >
                          <Heading size="sm" color="red.400">
                            Danger zone
                          </Heading>
                          <Text fontSize="sm" color="fg.muted">
                            Archiving removes this project from the
                            switcher. Within 14 days an admin can restore
                            it from the project picker; after that the
                            nightly cleanup permanently deletes its
                            clients, calls and appointments.
                          </Text>
                          <Button
                            variant="outline"
                            colorPalette="red"
                            alignSelf="flex-start"
                            onClick={() => setArchiveOpen(true)}
                          >
                            Archive project
                          </Button>
                        </Stack>
                      ) : null}
                    </Stack>
                  </Tabs.Content>
                </Tabs.Root>
              )}
            </Dialog.Body>

            <Dialog.Footer>
              <HStack gap={2}>
                <Button
                  variant="outline"
                  onClick={cancel}
                  disabled={update.isPending}
                >
                  Cancel
                </Button>
                <Button
                  colorPalette="blue"
                  onClick={save}
                  loading={update.isPending}
                  disabled={!dirty}
                >
                  Save
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>

      <ConfirmDialog
        open={archiveOpen}
        title="Archive project?"
        description={`Archive ${(
          settings?.callerBusinessName?.trim() || "this project"
        )}? Its clients, calls and appointments are hidden everywhere; an admin can restore from the project picker within 14 days.`}
        confirmLabel="Archive project"
        submitting={archiveProject.isPending}
        onOpenChange={(o) => {
          if (!o) setArchiveOpen(false);
        }}
        onConfirm={() => {
          if (!activeWs || !me?.lastActiveProjectId) return;
          archiveProject.mutate(
            { workspaceId: activeWs.id, projectId: me.lastActiveProjectId },
            {
              onSuccess: () => {
                setArchiveOpen(false);
                showToast("success", "Project archived");
                onOpenChange(false);
                window.location.href = "/projects";
              },
              onError: (err) => {
                setArchiveOpen(false);
                showToast(
                  "error",
                  err instanceof Error ? err.message : "Failed to archive",
                );
              },
            },
          );
        }}
      />
    </Dialog.Root>
  );
}
